import { LOCALES } from "../languageDetection";

/**
 * 静态资源和API等前缀
 */
export const IGNORE_PREFIXES = [
  "/assets",
  "/_next",
  "/api",
  "/__/auth",
  "/favicon.ico",
  "/robots.txt",
  "/public",
  "/assets",
  "/manifest.webmanifest",
  "/.well-known",
  "/about/assets",
  "/about/_next",
  "/about/favicon",
  "/terms_of_service.html",
  "/privacy_policy.html",
  "/google457883b181ff081f.html",
  "/landing-page",
  "/llms.txt",
  "/test-bridge",
  "/download-url",
  "/prefetch.html",
  // quiz 应用反代静态根：HTML 内写死 /quizzes/...、/images/quizzes/...
  // 不能进 i18n 307，否则 /zh-tw/quizzes/*.mp4 无 rewrite → HTML 404（视频只闪 poster）
  "/quizzes",
  "/images/quizzes",
  // "/editorial-standards.html",
];

/**
 * 静态资源后缀
 */
export const IGNORE_EXTENSIONS = [
  ".ico",
  ".png",
  ".jpg",
  ".jpeg",
  ".svg",
  ".gif",
  ".webp",
  ".js",
  ".css",
  ".woff",
  ".woff2",
  ".ttf",
  ".eot",
  ".xml",
  // quiz 肖像循环视频等；缺省会被 i18n 307 到 /:locale/... 后 404
  ".mp4",
  ".webm",
];

/**
 * 主站页面段
 */
export const ABOUT = "about";

/**
 * 官网路由列表（locale 在这些路由前面）
 * 例如：/zh/blog、/blog、/zh-tw/features、/features
 *
 * - check-up：列表页 `www.edwealth.ai/check-up`（homesite）+ 详情
 *   `www.edwealth.ai/check-up/{id}`（token-seek 反代到 quiz 应用）
 * - quiz：旧详情路径，next.config redirects 301 → `/check-up/{id}`；
 *   保留在白名单，避免 301 前 onboarding gate 误送 login
 * - share：roast 分享页 `/share/roast` 走 homesite（同 check-up 例外），
 *   不注入 device 段（否则 `/en/desktop/share/roast` 绕过 beforeFiles
 *   rewrites 后 404）；token-seek 自身无 `/share` 产品路由，整段放行安全
 *
 * 两者都是公开主站内容，在 onboarding gate 的 isMainSite 判断中同等放行。
 *
 * - i：邀请分享链接 `/i/{code}` 走 homesite 中转页（EDG-557），理由同 share：
 *   token-seek 无 `/i` 产品路由，整段放行；不放行会被注入 device 段而绕过 rewrites
 *
 * savings / cashflow / moneybench 无产品子树，与 rsu 一样按第一段整棵树放行。
 * **不要**把 invest 放进本名单：`/invest/*` 仍是须登录的产品壳。
 * 裸 `/invest` 见 `MAIN_SITE_EXACT_FIRST_SEGMENTS`。
 */
export const MAIN_SITE_ROUTES = [
  "article",
  "author",
  "pricing",
  "financial-fitness",
  "quiz",
  "check-up",
  // roast 分享页 /share/roast 走 homesite（同 check-up 例外）
  "share",
  "rsu",
  "unsubscribe",
  "savings",
  "cashflow",
  "moneybench",
  "i",
];

/**
 * 去掉 locale 后恰好一段才算官网（rewrite + 免登录 + 不注入 device）。
 * `/invest`、`/zh-tw/invest` → 官网营销页；`/invest/opportunity` 仍是产品。
 * `/about`、`/zh-tw/about` → about-edwealth 文章 SEO 别名（EDG-595）；`/about/*` 仍是 homesite basePath。
 */
export const MAIN_SITE_EXACT_FIRST_SEGMENTS = ["invest", "about"] as const;

/**
 * 支持的语言列表
 */
export const SUPPORTED_LOCALES = Object.values(LOCALES);

/**
 * 允许绕过basic auth的bot列表（小写）
 */
export const ALLOWED_BOT_KEYWORDS = [
  // 原有的Ahrefs bots（SEO分析工具）
  "ahrefsbot",
  "ahrefssiteaudit",

  // 新增的AI和数据收集爬虫
  "imagesiftbot", // 图像分析bot
  "diffbot", // 内容提取bot
  "facebookbot", // Facebook爬虫
  "applebot-extended", // Apple扩展爬虫
  "meta-externalagent", // Meta外部代理
  "ccbot", // Common Crawl bot
  "cohere-ai", // Cohere AI爬虫
  "gptbot", // OpenAI GPT bot
  "perplexitybot", // Perplexity AI bot
  "claudebot", // Anthropic Claude bot
  "bytespider", // ByteDance爬虫
  "meta-externalfetcher", // Meta外部获取器
  "chatgpt-user", // ChatGPT用户代理
  "omgilibot", // Omgili搜索bot
  "anthropic-ai", // Anthropic AI爬虫

  // 社媒链接预览爬虫：邀请链接 /i/{code} 在测试环境（basic auth）也要能抓到 OG 卡片（EDG-557）。
  // 与 homesite `isShareOgCrawler` 对齐
  "facebookexternalhit",
  "twitterbot",
  "linkedinbot",
  "slackbot",
  "discordbot",
  "telegrambot",
  "whatsapp",
] as const;
